import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { X, Save, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = [
    { name: 'Blu', value: '#3B82F6' },
    { name: 'Verde', value: '#10B981' },
    { name: 'Rosso', value: '#EF4444' },
    { name: 'Giallo', value: '#F59E0B' },
    { name: 'Viola', value: '#8B5CF6' },
    { name: 'Rosa', value: '#EC4899' },
    { name: 'Indaco', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' }
];

const CATEGORIES = ['Yoga', 'Pilates', 'CrossFit', 'Spinning', 'Zumba', 'Aerobica', 'Functional', 'Boxe', 'Kick Boxing', 'Calisthenics', 'Altro'];

export default function EditCourseModal({ isOpen, course, onClose, onSuccess, embedded = false }) {
    const { getAuthenticatedApi } = useAuth();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        title: course.title || '',
        description: course.description || '',
        category: course.category || '',
        color: course.color || '#3B82F6',
        status: course.status || 'active'
    });
    
    // Stati per logo upload
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(
        course.logo_filename 
            ? `/api/serve_image.php?filename=${course.logo_filename}&type=logo&v=${Date.now()}` 
            : null
    );
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [deletingLogo, setDeletingLogo] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError(null);
    };
    
    const handleLogoFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Validazione tipo file
        const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Tipo file non supportato. Usa PNG, JPG o WEBP');
            return;
        }
        
        // Validazione dimensione (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File troppo grande. Massimo 2MB');
            return;
        }
        
        setLogoFile(file);
        
        // Crea preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };
    
    const handleUploadLogo = async () => {
        if (!logoFile || !course.id) return;
        
        try {
            setUploadingLogo(true);
            const api = getAuthenticatedApi();
            
            const formData = new FormData();
            formData.append('logo', logoFile);
            
            const response = await api.post(
                `/course_logo_upload.php?course_id=${course.id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.success) {
                const timestamp = response.data.timestamp || Date.now();
                setLogoPreview(`/api/serve_image.php?filename=${response.data.filename}&type=logo&v=${timestamp}`);
                setLogoFile(null);
                toast.success('Logo caricato con successo!');
                // Refresh lista corsi
                onSuccess();
            }
        } catch (err) {
            console.error('Errore upload logo corso:', err);
            toast.error(err.response?.data?.error || 'Errore nel caricamento del logo');
        } finally {
            setUploadingLogo(false);
        }
    };
    
    const handleDeleteLogo = async () => {
        // Caso 1: Logo solo in preview (non ancora caricato) - Rimuovi solo localmente
        if (logoFile && !course.logo_filename) {
            setLogoFile(null);
            setLogoPreview(null);
            toast.success('Preview logo rimossa');
            return;
        }
        
        // Caso 2: Logo già sul server - Chiedi conferma ed elimina dal server
        if (!window.confirm('Vuoi eliminare il logo del corso?')) {
            return;
        }
        
        try {
            setDeletingLogo(true);
            const api = getAuthenticatedApi();
            
            await api.delete(`/course_logo_upload.php?course_id=${course.id}`);
            
            setLogoPreview(null);
            setLogoFile(null);
            toast.success('Logo eliminato con successo!');
            // Refresh lista corsi
            onSuccess();
        } catch (err) {
            console.error('Errore eliminazione logo corso:', err);
            toast.error(err.response?.data?.error || 'Errore nell\'eliminazione del logo');
        } finally {
            setDeletingLogo(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Inserisci un titolo per il corso');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const api = getAuthenticatedApi();
            
            const updateData = {
                title: formData.title.trim(),
                description: formData.description.trim() || null,
                category: formData.category || null,
                color: formData.color,
                status: formData.status
            };
            
            await api.put(`/gym_courses.php?action=update_course&id=${course.id}`, updateData);
            onSuccess();
            
        } catch (err) {
            console.error('Errore aggiornamento corso:', err);
            setError(err.response?.data?.error || 'Errore nell\'aggiornamento del corso');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const shellClass = embedded
        ? 'mb-6 rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900'
        : 'bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl my-8';

    const body = (
            <div className={shellClass}>
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Modifica corso
                    </h3>
                    {!embedded && (
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <X size={24} />
                        </button>
                    )}
                </div>

                {error && (
                    <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                            Titolo Corso *
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                            Descrizione
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Categoria
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">Nessuna</option>
                                {CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                                Stato
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="active">Attivo</option>
                                <option value="paused">In Pausa</option>
                                <option value="cancelled">Annullato</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-1">
                            Colore
                        </label>
                        <div className="flex gap-2">
                            {COLORS.map(color => (
                                <button
                                    key={color.value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                                        formData.color === color.value ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent'
                                    }`}
                                    style={{ backgroundColor: color.value }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Logo Corso */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                            Logo Corso (opzionale)
                        </label>
                        
                        <div className="space-y-3">
                            {/* Preview Logo */}
                            {logoPreview && (
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <img 
                                            src={logoPreview} 
                                            alt="Logo corso" 
                                            className="w-20 h-20 object-cover rounded-lg border-2 border-gray-300 dark:border-gray-700"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleDeleteLogo}
                                        disabled={deletingLogo}
                                        className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 size={16} />
                                        <span className="text-sm">Rimuovi</span>
                                    </button>
                                </div>
                            )}
                            
                            {/* Upload Input */}
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={handleLogoFileChange}
                                    className="hidden"
                                    id="course-logo-input"
                                />
                                <label
                                    htmlFor="course-logo-input"
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:brightness-110 text-gray-700 dark:text-white/85 rounded-lg cursor-pointer transition-colors"
                                >
                                    <ImageIcon size={18} />
                                    {logoPreview ? 'Cambia Logo' : 'Scegli Logo'}
                                </label>
                                
                                {logoFile && (
                                    <button
                                        type="button"
                                        onClick={handleUploadLogo}
                                        disabled={uploadingLogo}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {uploadingLogo ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                                Caricamento...
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={18} />
                                                Carica
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            
                            <p className="text-xs text-gray-500 dark:text-white/65">
                                PNG, JPG o WEBP. Max 2MB, 1500x1500px. Consigliato: 200-400px
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-700 dark:text-white/85 hover:bg-gray-100 dark:hover:brightness-110 rounded-lg transition-colors"
                        >
                            Annulla
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Salvataggio...
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Salva Modifiche
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
    );

    if (embedded) return body;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            {body}
        </div>
    );
}

